import React, { useRef, useEffect } from "react";
import {Icon} from "@/components/Icon.tsx";
import { sseRequest, stopSseRequest } from "@/store/sse";
import { constants } from "@/constants";

// Speech to Text
import {
  camelToSnakeObj,
  getSession,
  getTimestamp,
  snakeToCamelObj,
} from "@/utils/utils";
import "regenerator-runtime/runtime.js";
import "./styles.scss";
import "react-toastify/dist/ReactToastify.css";
import {
  ChatBotProps,
  ChatResponse,
  TForm,
  Msg,
  msgActionReaction,
  msgActionStateSending,
  msgActionStateSent,
  msgActionVisitorData,
  roleAssistant,
  roleLeadForm,
  roleUser,
  tabContact,
  tabHome,
  tabMessages,
} from "@/components/dialog/types";
import {
  profileIconRenderer,
  msgRenderer,
} from "@/components/dialog/renderers";
import { contrastColor, hexToRGB } from "@/utils/colors";
import { forceScrolling } from "@/components/dialog/helpers";
import InputField from "@/components/dialog/InputField";
import Form from "@/components/dialog/widgets/Form";
import { toast, ToastContainer } from "react-toastify";
import TabHome from "@/components/dialog/widgets/TabHome";

const tabs = (showHome: boolean) => {
  let result = [
    showHome
      ? { state: tabHome, title: "Home", icon: "fluent:home-20-regular" }
      : null,
    { state: tabMessages, title: "Messages", icon: "ph:chat-text" },
    {
      state: tabContact,
      title: "Account",
      icon: "fluent:person-circle-20-regular",
    },
  ];

  return result.filter((item) => item);
};
export const botIcons = (
  scale?: number,
): { [key: string]: { name: string; icon: React.ReactElement } } => {
  scale = scale ? scale : 1;
  return {
    "1": {
      name: "carbon:chat-bot",
      icon: (
        <Icon className="mt-1.5" icon="carbon:chat-bot" width={30 * scale} />
      ),
    },
    "2": {
      name: "fluent-mdl2:chat-bot",
      icon: (
        <Icon
          className="ml-[8%]"
          icon="fluent-mdl2:chat-bot"
          width={35 * scale}
        />
      ),
    },
    "3": {
      name: "fluent:bot-20-regular",
      icon: (
        <Icon className="" icon="fluent:bot-20-regular" width={35 * scale} />
      ),
    },
    "4": {
      name: "lucide:bot",
      icon: <Icon className="mb-1" icon="lucide:bot" width={30 * scale} />,
    },
    "5": {
      name: "file-icons:imgbot",
      icon: <Icon icon="file-icons:imgbot" width={22 * scale} />,
    },
    "6": {
      name: "mdi:customer-service",
      icon: <Icon icon="mdi:customer-service" width={32 * scale} />,
    },
    "7": {
      name: "fluent:person-20-regular",
      icon: <Icon icon="fluent:person-20-regular" width={32 * scale} />,
    },
    "8": {
      name: "streamline:interface-help-customer-support-2-customer-headphones-headset-help-microphone-phone-person-support",
      icon: (
        <Icon
          icon="streamline:interface-help-customer-support-2-customer-headphones-headset-help-microphone-phone-person-support"
          width={28 * scale}
        />
      ),
    },
  };
};

const notifySuccess = (msg: string) => {
  toast.success(msg, {
    containerId: "toastDialog",
    position: "top-right",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: false,
    progress: undefined,
    theme: "light",
  });
};

let msgWidth = 300;
let msgWidthInited = false;
let answerMsg: Msg | null = null;

let conversationHistoryLoading = false;
let historyCache: { [key: string]: Msg[] } = {};

function getHistoryCache(token: string): Msg[] | null {
  let hist = historyCache[token];
  return hist !== undefined ? hist : null;
}

function setHistoryCache(token: string, messages: Msg[]) {
  historyCache[token] = messages;
}

function updateMsgWidth() {
  let node = document.querySelector(".wz-msg-text");
  if (node && node.clientWidth > 300) {
    msgWidth = node.clientWidth;
    return true;
  }
  return false;
}

function isAllowedUseAction(action: string, delay: number): boolean {
  // delay in seconds
  let key = `_wizychat_delay_action-${action}`;
  let unixNow = Math.floor(Date.now() / 1000);
  let val = window.localStorage.getItem(key);

  if (val && parseInt(val) && unixNow - parseInt(val) < delay) {
    return false;
  }

  window.localStorage.setItem(key, unixNow.toString());
  return true;
}

const ChatbotDialog = (props: ChatBotProps) => {
  let messagesContainerRef = useRef();

  const [tab, setTab] = React.useState(
    props.design.tabHomeShow ? tabHome : tabMessages,
  );
  const [progress, setProgress] = React.useState(false);
  const [viewMsgList, setMsgList] = React.useState(
    props.messages ? props.messages : [],
  );
  const [contactsForm, setContactsForm] = React.useState({
    fields: props.design.profileFields,
  } as TForm);

  const loadHistory = async () => {
    try {
      conversationHistoryLoading = true;
      let res = await fetch(
        `${constants.url.API_PREFIX}chat/conversation?token=${
          props.token
        }&session=${getSession(props.token)}`,
      );

      let hist = await res.json();
      if (hist !== null) {
        setHistoryCache(props.token, hist);
        setMsgList(hist);

        if (hist.length == 0 && (props.design.initialMessage)) {
          setMsgList([
            {
              id: 0,
              text: props.design.initialMessage,
              role: roleAssistant,
              time: getTimestamp(),
            },
          ]);
        }
      }
    } catch (e) {}
    conversationHistoryLoading = false;
  };

  const sendQuestion = (question: string) => {
    if (!question) {
      return;
    }
    let dt = Date.now();
    setMsgList((list: Msg[]) => {
      let newList = [...list];

      newList.push({
        id: dt,
        text: question,
        role: roleUser,
      });

      answerMsg = {
        id: dt + 1,
        text: "",
        role: roleAssistant,
        streaming: true,
      };
      newList.push(answerMsg);

      return newList;
    });

    setProgress(true);
    let msgID = 0;

    sseRequest(
      `${constants.url.API_PREFIX}chat`,
      {
        token: props.token,
        question: question.trim(),
        session: getSession(props.token),
        history: viewMsgList,
      },
      (res: any) => {
        if (!answerMsg) {
          return;
        }
        if (
          (res.mode === "answer" || res.mode === "answer_single") &&
          res.data
        ) {
          answerMsg.text += res.data;
          if (msgID < 5) {
            answerMsg.text = answerMsg.text.replace(/^[,.!?:\s]*/, "");
          }
          msgID++;

          setMsgList((list: Msg[]) => {
            let newList = [...list];
            if (newList.length) {
              newList[newList.length - 1] = answerMsg as any;
            }
            return newList;
          });
        }
        if (res.mode === "answer_done" || res.mode === "answer_single") {
          updateMsgWidth();
          stopGenerating(false, res);
        }
      },
      async (res) => {
        stopGenerating(false);
      },
      () => {
        setProgress(false);
      },
    );
  };

  const stopGenerating = (abortSse: boolean, res?: ChatResponse) => {
    if (abortSse) {
      stopSseRequest();
      setProgress(false);
    }

    setMsgList((list: Msg[]) => {
      let newList = [...list];
      let msg: Msg = { id: Date.now() } as Msg;
      if (newList.length && answerMsg) {
        msg = newList[newList.length - 1];
      }

      if (res?.message) {
        msg = Object.assign(msg, snakeToCamelObj(res.message)) as Msg;
      }

      if (res && (res.docs || res.buttons || res.sources)) {
        msg.buttons = res?.buttons;
        msg.sources = res?.sources;
        msg.sourceID = res?.sources ? 0 : undefined;
        msg.sourcesExpanded = props.design.sourcesAutoExpand;
      }

      if (answerMsg) {
        msg.streaming = false;
        newList[newList.length - 1] = msg as any;
        answerMsg = null; // text streaming ended
      } else {
        if (msg.role === roleLeadForm) {
          isAllowedUseAction("leads_form", 10800) && newList.push(msg); // delay 3 hours
        } else {
          newList.push(msg);
        }
      }

      return newList;
    });
  };

  const onMsgAction = async (action: string, payload: Msg) => {
    updateMessage(payload);

    let body: any | null = {
      action: action,
      token: props.token,
      session: getSession(props.token),
    };
    if (action == msgActionVisitorData || action == msgActionReaction) {
      body.message = payload;
    }
    body = camelToSnakeObj(body);

    try {
      payload.actionState = msgActionStateSending;
      let res = await fetch(`${constants.url.API_PREFIX}chat/data`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      payload.actionState = msgActionStateSent;
      updateMessage(payload);
    } catch (e) {}
  };

  const updateMessage = (msg: Msg) => {
    setMsgList((list: Msg[]) => {
      let newList = [...list];
      let i = newList.findIndex((item) => {
        return item.id === msg.id;
      });
      if (i !== -1) {
        newList[i] = msg;
      }
      return newList;
    });
  };

  useEffect(() => {
    setTab(props.design.tabHomeShow ? tabHome : tabMessages);
  }, [props.design.tabHomeShow]);

  // AutoScrolling
  useEffect(() => {
    forceScrolling(messagesContainerRef);
  }, [viewMsgList, tab]);

  useEffect(() => {
    setTimeout(() => {
      forceScrolling(messagesContainerRef);
    }, 100);
  }, [progress, tab]);

  useEffect(() => {
    if (!msgWidthInited) {
      updateMsgWidth();
      msgWidthInited = true;
      setMsgList((list: Msg[]) => {
        let newList = [...list];
        return newList;
      });
    }
  }, [viewMsgList]);

  if (
    !props.messages &&
    getHistoryCache(props.token) == null &&
    !conversationHistoryLoading
  ) {
    loadHistory();
    setTimeout(() => {
      forceScrolling(messagesContainerRef);
    }, 300);
  }

  useEffect(() => {
    if (viewMsgList.length == 0) {
      let hist = getHistoryCache(props.token);
      if (hist !== null) {
        setMsgList(hist);
      }
    }
  }, []);

  let rgb = hexToRGB(props.design.profileIconBgColor);
  let headerContrastColor = contrastColor(rgb.r, rgb.g, rgb.b);

  return (
    <div className="h-full w-full flex flex-col shadow-lg relative rounded-lg bg-white">
      <div
        className="flex items-center justify-between p-2 rounded-t-lg"
        style={{
          backgroundColor: props.design.profileIconBgColor,
          color: headerContrastColor,
        }}
      >
        {/* user info */}
        <div className="flex items-center">
          {tab == tabMessages && (
            <Icon
              onClick={() => {
                setTab(tabHome);
              }}
              className="hover:opacity-60 cursor-pointer mx-1"
              icon="fluent:ios-arrow-ltr-24-filled"
              width="24px"
            />
          )}

          {props.design.showProfileIconHeader && profileIconRenderer(props, 1, "w-[unset] h-[unset]")}

          <div className="ml-2">
            {!!props.design.title && (
              <div className="font-semibold">{props.design.title}</div>
            )}
            {props.design.showOnline && <div className="text-xs">Online</div>}
          </div>
        </div>
        {/* end user info */}
        {/* chat box action */}
        <div>
          {props.showExpandBtn && (
            <button
              className="inline-flex p-2 hover:opacity-50"
              onClick={props.onExpand}
            >
              <Icon icon="fluent:arrow-expand-16-regular" width="25" />
            </button>
          )}
          <button
            className="inline-flex p-2 hover:opacity-50"
            aria-label="refresh-dialog"
            onClick={props.onRefresh}
          >
            <Icon icon="fluent:arrow-sync-16-regular" width="25" />
          </button>
          {!props.hideCloseBtn && (
            <button
              onClick={() => {
                props.onClose();
              }}
              className="inline-flex p-2 hover:opacity-50"
              aria-label="close-dialog"
              type="button"
            >
              <Icon icon="fluent:dismiss-16-regular" width="25" />
            </button>
          )}
        </div>
        {/* end chat box action */}
      </div>

      <div
        ref={messagesContainerRef as any}
        className="dialog-scroll-area bg-white flex-1 overflow-y-auto overflow-x-hidden pr-1.5"
        style={{
          backgroundColor:
            tab == tabMessages
              ? "white"
              : props.design.profileIconBgColor + "10",
        }}
      >
        <ToastContainer
          enableMultiContainer
          containerId={"toastDialog"}
          position={"top-right"}
        />

        {tab == tabMessages && (
          <div className="h-full px-2 py-4 border-t-2">
            {viewMsgList.map((msg: Msg, index: number) => {
              return msgRenderer(
                props,
                msg,
                msgWidth,
                index,
                onMsgAction,
                updateMessage,
              );
            })}
          </div>
        )}

        {tab != tabMessages && (
          <div className="relative h-full">
            {tab == tabHome && (
              <TabHome chatbotProps={props} onTabChange={setTab} />
            )}

            {tab == tabContact && (
              <div className="h-full flex items-center justify-center z-[9999]">
                <Form
                  form={contactsForm}
                  action="tab_contacts"
                  onChange={(form: TForm) => {
                    setContactsForm(Object.assign({}, form));
                  }}
                  onAction={() => {
                    notifySuccess("Thank you");
                    setTimeout(() => {
                      setTab(tabMessages);
                    }, 2000);
                  }}
                  bgColor="#ffffff"
                  token={props.token}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-b-lg">
        {tab == tabMessages && (
          <InputField
            chat={props}
            stopGenerating={stopGenerating}
            sendQuestion={sendQuestion}
            progress={progress}
          />
        )}

        {tab != tabMessages && (
          <div className="flex justify-between">
            {tabs(props.design.tabHomeShow).map((tabItem: any) => {
              return (
                <div
                  key={tabItem.state}
                  className={
                    `relative flex flex-col items-center justify-center w-full hover:text-primary-600 hover:bg-primary-100 py-2 cursor-pointer ` +
                    (tabItem.state == tab ? "text-primary-600" : "")
                  }
                  onClick={() => {
                    setTab(tabItem.state);
                  }}
                >
                  <Icon
                    className="gialog-input-icon icon-default"
                    icon={tabItem.icon}
                    width="25"
                  />
                  <span>{tabItem.title}</span>
                  <div
                    className="absolute w-full h-full top-0 left-0"
                    style={{
                      backgroundColor: props.design.profileIconBgColor,
                      opacity: 0.1,
                    }}
                  ></div>
                </div>
              );
            })}
          </div>
        )}

        {!props.design.hidePoweredBy && (
          <div
            className="w-full flex justify-center text-sm text-black-600 py-1 select-none bg-slate-100 border-t"
            dangerouslySetInnerHTML={{ __html: props.design.footerHtml }}
          ></div>
        )}
      </div>
    </div>
  );
};

export default ChatbotDialog;
